import {
  Component,
  ElementRef,
  computed,
  inject,
  input,
  model,
  signal,
  viewChild
} from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { SortQualifier } from '@core/filters/path-filter/dsl/search-bar';
import { FilterVocabulary } from '@core/filters/path-filter/vocabulary/vocabulary';
import { CurrentLangService } from '@core/services/common/current-lang.service';
import { ICONS } from '@core/services/icons/icons.service';

import { DropdownComponent } from '@shared/components/dropdown/dropdown.component';
import { FocusFirstDirective } from '@shared/directives/focus-first.directive';
import { AvailableLanguages } from '@shared/types/translation.type';

import { PATH_FILTER_I18N, sortableLabel } from '../path-filter-labels';
import { SortOption, sameSortTarget, sortOptions } from './sort-options';

@Component({
  selector: 'app-path-filter-sort',
  imports: [TranslatePipe, DropdownComponent, FocusFirstDirective],
  templateUrl: './path-filter-sort.component.html',
  styles: `
    :host {
      display: inline-flex;
    }
  `
})
export class PathFilterSortComponent {
  vocabulary = input.required<FilterVocabulary>();
  sort = model.required<SortQualifier[]>();

  protected readonly ICONS = ICONS;
  private readonly translate = inject(TranslateService);
  private readonly currentLang = inject(CurrentLangService).currentLang;
  private readonly sortButton = viewChild.required<ElementRef<HTMLElement>>('sortButton');

  protected readonly menuOpen = signal(false);
  protected readonly closeMenu = () => this.menuOpen.set(false);

  protected readonly options = computed(() => {
    const lang = this.currentLang() as AvailableLanguages;
    return sortOptions(
      this.vocabulary(),
      (sortable) => sortableLabel(sortable, this.vocabulary(), lang, this.translate),
      (aggregate) =>
        this.translate.instant(`${PATH_FILTER_I18N}.sort.aggregate.${aggregate}`)
    );
  });

  protected readonly active = computed(() =>
    this.sort().map((qualifier, index) => ({
      index,
      order: qualifier.order,
      label:
        this.options().find((option) => sameSortTarget(option.target, qualifier))?.label ??
        qualifier.field
    }))
  );

  protected isSorted(option: SortOption): boolean {
    return this.sort().some((qualifier) => sameSortTarget(qualifier, option.target));
  }

  protected onMenuKey(event: KeyboardEvent): void {
    // The overlay stops key events at its panels, so its own Escape listener never sees this one.
    if (event.key !== 'Escape') return;
    this.closeMenu();
    this.sortButton().nativeElement.focus();
  }

  /** Picking a field already sorted on moves it to the end rather than sorting on it twice. */
  protected add(option: SortOption): void {
    this.sort.set([
      ...this.sort().filter((qualifier) => !sameSortTarget(qualifier, option.target)),
      { ...option.target, order: 'asc' }
    ]);
  }

  protected flip(index: number): void {
    this.sort.set(
      this.sort().map((qualifier, position) =>
        position === index
          ? { ...qualifier, order: qualifier.order === 'asc' ? 'desc' : 'asc' }
          : qualifier
      )
    );
  }

  protected remove(index: number): void {
    this.sort.set(this.sort().filter((_, position) => position !== index));
  }

  protected clear(): void {
    this.sort.set([]);
    this.closeMenu();
  }
}
